// src/modules/units/tyres/views/retread-batch/retreadbatchlist.js
//
// Orchestrator for the retread-batch list: fetch the batches (and the suppliers,
// for the filter and for turning supplierId into a name), render via the pure
// view, and refetch whenever a batch changes. A thin fetch-and-swap shell — all
// markup lives in retreadbatchlist.view.js.
//
// Filters are sent to the server (status, supplierId): the backend owns customer
// scoping and paging, so re-fetching on a filter change keeps this list
// authoritative rather than filtering a stale local copy. Read-only for now —
// creating and dispatching batches arrive with the write forms.

import { fetchAllRetreadBatches } from '../retread-batch.api.js';
import { fetchSuppliers } from '../suppliers.api.js';
import { listHTML, rowHTML, stateRowHTML, sortBatchesForDisplay } from './retreadbatchlist.view.js';
import { canManageBatches } from '../../shared/retread-batch-access.js';
import { onTyreEvent, TYRE_EVENTS } from '../../shared/tyre-events.js';

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

/**
 * @param {HTMLElement} viewEl controller view slot (emptied first)
 * @param {object} ctx
 * @param {(batch:object)=>void} [ctx.onOpen] a batch row was activated
 * @param {() => void} [ctx.onNew] the New batch button was pressed (admins only)
 * @returns {() => void} teardown
 */
export function renderRetreadBatchList(viewEl, ctx = {}) {
  let disposed = false;
  const state = { status: '', supplierId: '' };
  let suppliers = [];
  let suppliersById = new Map();

  const root = el(listHTML({
    tbodyInner: stateRowHTML('loading', 'Loading retread batches…'),
    canManage: canManageBatches()
  }));
  const rowsEl = root.querySelector('[data-rb-rows]');
  const statusSel = root.querySelector('[data-rb-filter="status"]');
  const supplierSel = root.querySelector('[data-rb-filter="supplier"]');

  function setRows(html) { rowsEl.innerHTML = html; }

  function supplierName(supplierId) {
    if (supplierId === undefined || supplierId === null) return '';
    const s = suppliersById.get(String(supplierId));
    return s ? (s.supplierName || 'Unnamed supplier') : '';
  }

  async function loadSuppliers() {
    // Best-effort: the list still works without names, so a supplier fetch failure
    // downgrades to showing ids rather than blocking the batches.
    const res = await fetchSuppliers({ sort: 'supplierName' });
    if (disposed || !res.success) return;
    suppliers = Array.isArray(res.data?.results) ? res.data.results : [];
    suppliersById = new Map(suppliers.map((s) => [String(s.supplierId), s]));
    // Repopulate the supplier filter now that names are in.
    if (supplierSel) {
      const current = state.supplierId;
      supplierSel.innerHTML =
        `<option value="">All suppliers</option>` +
        suppliers.map((s) =>
          `<option value="${s.supplierId}"${String(s.supplierId) === String(current) ? ' selected' : ''}>` +
          `${(s.supplierName || 'Unnamed supplier').replace(/</g, '&lt;')}</option>`
        ).join('');
    }
  }

  async function loadBatches() {
    setRows(stateRowHTML('loading', 'Loading retread batches…'));
    // Page to completion so the list is never silently truncated at the endpoint's
    // default page size, then order newest-first on the client (see
    // sortBatchesForDisplay) rather than relying on a server sort direction.
    const res = await fetchAllRetreadBatches({
      status: state.status || undefined,
      supplierId: state.supplierId || undefined
    });
    if (disposed) return;

    if (!res.success) {
      setRows(stateRowHTML('error', res.message || 'Could not load retread batches.', { retry: true }));
      return;
    }

    const batches = sortBatchesForDisplay(res.batches);
    if (!batches.length) {
      setRows(stateRowHTML('empty', 'No retread batches match this filter.'));
      return;
    }

    setRows(batches.map((b) => rowHTML(b, { supplierName: supplierName(b.supplierId) })).join(''));
  }

  // Filters re-fetch from the server.
  statusSel?.addEventListener('change', () => { state.status = statusSel.value; loadBatches(); });
  supplierSel?.addEventListener('change', () => { state.supplierId = supplierSel.value; loadBatches(); });

  // New batch (only present for administrators — see listHTML canManage).
  root.querySelector('[data-rb-new]')?.addEventListener('click', () => {
    if (typeof ctx.onNew === 'function') ctx.onNew();
  });

  rowsEl.addEventListener('click', (e) => {
    if (e.target.closest('[data-rb-retry]')) { loadBatches(); return; }
    const row = e.target.closest('[data-id]');
    if (!row) return;
    const id = row.dataset.id;
    if (typeof ctx.onOpen === 'function') {
      // Hand back the freshest row we have; the detail refetches by id anyway.
      const batch = { retreadBatchId: id };
      ctx.onOpen(batch);
    }
  });

  viewEl.replaceChildren(root);

  // Suppliers first (names + filter), then batches; refetch on any batch change,
  // and on a tyre CHANGE too, since receiving a delivery moves tyres and updates
  // the batch counts these rows show.
  loadSuppliers().then(() => { if (!disposed) loadBatches(); });
  const offBatch = onTyreEvent(TYRE_EVENTS.RETREAD_BATCH_CHANGED, loadBatches);
  const offTyre = onTyreEvent(TYRE_EVENTS.CHANGED, loadBatches);

  return () => { disposed = true; offBatch(); offTyre(); };
}
